/**
 * SQL 작성 — 요구 내용 → 유사 쿼리 → 작성 → 실행(docs/features/26-sql-author.md).
 *
 * 쓰는 사람: SQL 을 잘 모르는 업무 사용자(오류 없는 쿼리를 받아 다듬는다) · 개발자·운영자(긴 쿼리를 고친다).
 * 틀은 SQL 튜닝·진단 리포트 창과 같다(왼쪽 작업 목록 · 단계 리본 · 이력). 모든 단계는 사람도 한다(AI 개발 가이드라인 2):
 *   ① 요구 내용 — 사람
 *   ② 유사 쿼리 — 기계가 찾고(DB 공유 풀 + 앱 안 자산, 값은 가려서) 사람이 맞음/아님을 고른다. 「맞음」 만 ③ 의 참고 자료다
 *   ③ 작성 — AI 가 쓰고(또는 사람이) **기계가 실행계획으로 확인**한다. 실패하면 AI 가 오류를 보고 고친다, 끝내 안 되면 「판단 불가」
 *   ④ 실행 — 위에서 몇 행만(단일 SELECT·시간 상한·중지). 전체는 편집기 새 탭으로 보낸다
 * 실행 결과 행은 이 화면에만 있다 — 저장하지 않고 AI 로도 보내지 않는다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  FiEdit3, FiPlus, FiCpu, FiSquare, FiSearch, FiPlay, FiCheckCircle, FiAlertTriangle, FiLayers, FiExternalLink, FiX, FiCheck, FiChevronDown, FiChevronRight, FiCopy,
} from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { monoWideText } from '../../utils/monoWide';
import * as api from '../../services/sqlAuthor';
import EditorConnectionChip from '../common/EditorConnectionChip';
import { maskForAgent } from '../../utils/sqlQualityMarks';
import AgentStrip from '../assistant/AgentStrip';
import '../sqlReview/SqlReviewModal.css';
import './SqlAuthorModal.css';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon, stepDirty } from '../common/WorkflowHead';

const STEPS = ['request', 'similar', 'write', 'run'];
const TABS = [...STEPS, 'history'];
const KEYWORDS = ['with', 'exists', 'scalar', 'row_number', 'analytic', 'union', 'case', 'outer_join', 'group_by', 'hierarchy'];
const ROW_CHOICES = [20, 50, 100, 199];
const SUPPORTED = ['oracle', 'tibero', 'postgresql'];
const emptyRequest = () => ({ mode: 'new', targetSql: '', requirement: '', schema: '', tables: [], columns: '', keywords: [], logic: '', refSql: '', refNote: '' });
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));

/** 스키마 고르기 + 테이블 찾아 넣기 — 연결의 카탈로그를 서버가 읽는다(이름·주석만) */
function TablePicker({ connectionId, schema, tables, onSchema, onTables, disabled }) {
  const { t } = useTranslation();
  const [schemas, setSchemas] = useState([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    if (!connectionId) { setSchemas([]); return; }
    let alive = true;
    api.lookupCatalog({ connectionId }).then((r) => {
      if (!alive) return;
      const list = r.schemas || [];
      setSchemas(list);
      // 스키마 이름의 대소문자는 DB 마다 다르다(Oracle EDU · PostgreSQL edu) — 목록에 있는 모양으로 맞춘다.
      // 연결을 바꿨는데 앞 연결의 스키마가 남아 있으면 새 연결의 것으로 바꾼다(2026-10-28)
      const same = v => list.find(s => s.toUpperCase() === String(v).toUpperCase()) || null;
      const dflt = r.defaultSchema ? (same(r.defaultSchema) || String(r.defaultSchema)) : '';
      if (!schema) { if (dflt) onSchema(dflt); } else if (list.length && !list.includes(schema)) onSchema(same(schema) || dflt);
    }).catch(err => alive && toast.error(formatApiError(err, tRef.current)));
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 연결이 바뀔 때만 스키마 목록을 다시 읽는다
  }, [connectionId]);
  const search = async (listAll) => {
    if (!connectionId) return;
    setBusy(true);
    try { setFound((await api.lookupCatalog({ connectionId, schema: schema || null, q: listAll ? '' : q })).tables || []); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const add = (name) => { if (!tables.includes(name)) onTables([...tables, name].slice(0, 20)); };
  return (
    <div className="sau-picker">
      <div className="stn-row">
        <label className="stn-row">{t('common:sau.schema')}
          <select value={schema} onChange={e => onSchema(e.target.value)} disabled={disabled}>
            <option value="">{t('common:sau.schemaAny')}</option>
            {schema && !schemas.includes(schema) && <option value={schema}>{schema}</option>}
            {schemas.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <input className="stn-grow" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:sau.tableSearchPh')} disabled={disabled}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); search(false); } }} />
        <button type="button" className="app-btn" onClick={() => search(false)} disabled={disabled || busy || !q.trim()}><FiSearch size={12} /> {t('common:sau.findTable')}</button>
        <button type="button" className="app-btn" onClick={() => search(true)} disabled={disabled || busy || !schema} title={t('common:sau.listTablesHint')}>{t('common:sau.listTables')}</button>
      </div>
      <div className="sau-chips">
        {tables.map(n => (
          <span key={n} className="sau-chip">{n}
            <button type="button" aria-label={t('common:ui.delete')} onClick={() => onTables(tables.filter(x => x !== n))} disabled={disabled}><FiX size={10} /></button>
          </span>
        ))}
        {!tables.length && <span className="stn-note">{t('common:sau.tablesEmpty')}</span>}
      </div>
      {found && (
        <ul className="sau-found">
          {found.map(f => {
            const name = `${f.owner}.${f.name}`;
            const on = tables.includes(name);
            return (
              <li key={name} className={on ? 'on' : ''} onClick={() => (on ? onTables(tables.filter(x => x !== name)) : add(name))}>
                {on ? <FiCheck size={11} /> : <FiPlus size={11} />} <b className="mono">{name}</b>{f.type === 'VIEW' ? <span className="stn-note"> VIEW</span> : null}
                {f.comment ? <span className="stn-note"> — {f.comment}</span> : null}
              </li>
            );
          })}
          {!found.length && <li className="stn-note">{t('common:sau.noTables')}</li>}
        </ul>
      )}
    </div>
  );
}
TablePicker.propTypes = {
  connectionId: PropTypes.string, schema: PropTypes.string, tables: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSchema: PropTypes.func.isRequired, onTables: PropTypes.func.isRequired, disabled: PropTypes.bool,
};

/** ① 요구 내용 — 새 작업과 1단계가 함께 쓴다 */
function RequestFields({ value, onChange, connectionId, getEditorSql, disabled }) {
  const { t } = useTranslation();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const fromEditor = (k) => {
    const got = getEditorSql ? getEditorSql() : null;
    if (!got || !got.sql) { toast.info(t('common:sau.editorEmpty')); return; }
    onChange({ ...value, [k]: got.sql });
  };
  const toggleKw = (k) => onChange({ ...value, keywords: value.keywords.includes(k) ? value.keywords.filter(x => x !== k) : [...value.keywords, k] });
  return (
    <>
      <div className="stn-row sau-mode" role="radiogroup" aria-label={t('common:sau.mode')}>
        {['new', 'modify'].map(m => (
          <label key={m} className={`sau-mode-opt${value.mode === m ? ' on' : ''}`}>
            <input type="radio" name="sau-mode" checked={value.mode === m} onChange={() => onChange({ ...value, mode: m })} disabled={disabled} />
            <b>{t(`common:sau.modes.${m}`)}</b><span className="stn-note">{t(`common:sau.modeHint.${m}`)}</span>
          </label>
        ))}
      </div>
      {value.mode === 'modify' && (
        <label className="stn-field"><span>{t('common:sau.targetSql')} <em className="sau-req">*</em>
          {getEditorSql && <button type="button" className="app-btn sau-inline" onClick={() => fromEditor('targetSql')} disabled={disabled}>{t('common:sau.fromEditor')}</button>}</span>
          <textarea className="srv-mono" rows={7} value={value.targetSql} onChange={set('targetSql')} disabled={disabled} />
        </label>
      )}
      <label className="stn-field"><span>{t('common:sau.requirement')} <em className="sau-req">*</em></span>
        <textarea rows={4} value={value.requirement} onChange={set('requirement')} placeholder={t(`common:sau.requirementPh.${value.mode}`)} disabled={disabled} />
      </label>
      <div className="stn-field"><span>{t('common:sau.tables')}</span>
        <TablePicker connectionId={connectionId} schema={value.schema} tables={value.tables} disabled={disabled}
          onSchema={schema => onChange({ ...value, schema })} onTables={tables => onChange({ ...value, tables })} />
        <span className="stn-note">{t('common:sau.tablesHint')}</span>
      </div>
      <label className="stn-field"><span>{t('common:sau.columns')}</span>
        <input value={value.columns} onChange={set('columns')} placeholder={t('common:sau.columnsPh')} disabled={disabled} />
      </label>
      <div className="stn-field"><span>{t('common:sau.keywords')}</span>
        <div className="sau-kws">
          {KEYWORDS.map(k => (
            <label key={k} className={`sau-kw${value.keywords.includes(k) ? ' on' : ''}`}>
              <input type="checkbox" checked={value.keywords.includes(k)} onChange={() => toggleKw(k)} disabled={disabled} /> {t(`common:sau.kw.${k}`)}
            </label>
          ))}
        </div>
      </div>
      <label className="stn-field"><span>{t('common:sau.logic')}</span>
        <textarea rows={3} value={value.logic} onChange={set('logic')} placeholder={t('common:sau.logicPh')} disabled={disabled} />
      </label>
      <div className="stn-grid2">
        <label className="stn-field"><span>{t('common:sau.refSql')}
          {getEditorSql && <button type="button" className="app-btn sau-inline" onClick={() => fromEditor('refSql')} disabled={disabled}>{t('common:sau.fromEditor')}</button>}</span>
          <textarea className="srv-mono" rows={5} value={value.refSql} onChange={set('refSql')} disabled={disabled} />
        </label>
        <label className="stn-field"><span>{t('common:sau.refNote')}</span>
          <textarea rows={5} value={value.refNote} onChange={set('refNote')} placeholder={t('common:sau.refNotePh')} disabled={disabled} />
        </label>
      </div>
    </>
  );
}
RequestFields.propTypes = {
  value: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, connectionId: PropTypes.string, getEditorSql: PropTypes.func, disabled: PropTypes.bool,
};

const requestOk = (r) => !!(r && r.requirement.trim() && (r.mode !== 'modify' || r.targetSql.trim()));

/** 유사 쿼리 한 건 — 출처·맞은 낱말·값 가림 표시, 맞음/아님 */
function SimilarCard({ it, onVerdict, onNote, onUse, disabled }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(it.verdict === 'fit');
  return (
    <div className={`stn-card sau-sim${it.verdict ? ` v-${it.verdict}` : ''}`}>
      <div className="stn-row stn-between">
        <span className="stn-row sau-sim-head" onClick={() => setOpen(o => !o)}>
          {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
          <span className={`sau-src s-${it.source}`}>{t(`common:sau.src.${it.source}`)}</span>
          <b className="sau-sim-title">{it.title}</b>
          {it.meta && <span className="stn-note">{it.meta}</span>}
        </span>
        <span className="stn-row">
          <button type="button" className={`app-btn sau-v${it.verdict === 'fit' ? ' on-fit' : ''}`} aria-pressed={it.verdict === 'fit'} onClick={() => onVerdict(it.verdict === 'fit' ? null : 'fit')} disabled={disabled}><FiCheck size={11} /> {t('common:sau.fit')}</button>
          <button type="button" className={`app-btn sau-v${it.verdict === 'unfit' ? ' on-unfit' : ''}`} aria-pressed={it.verdict === 'unfit'} onClick={() => onVerdict(it.verdict === 'unfit' ? null : 'unfit')} disabled={disabled}><FiX size={11} /> {t('common:sau.unfit')}</button>
        </span>
      </div>
      <div className="stn-row sau-sim-tags">
        {(it.matched || []).map(m => <span key={m} className="sau-tag">{m}</span>)}
        {it.masked && <span className="sau-tag sau-masked" title={t('common:sau.maskedHint')}>{t('common:sau.masked')}</span>}
      </div>
      {open && (
        <div className="sau-fade">
          <pre className="sau-sql">{it.sql}</pre>
          <div className="stn-row">
            <input className="stn-grow" value={it.note || ''} placeholder={t('common:sau.notePh')} onChange={e => onNote(e.target.value)} disabled={disabled} />
            <button type="button" className="app-btn" onClick={onUse} disabled={disabled} title={t('common:sau.useAsRefHint')}>{t('common:sau.useAsRef')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
SimilarCard.propTypes = { it: PropTypes.object.isRequired, onVerdict: PropTypes.func.isRequired, onNote: PropTypes.func.isRequired, onUse: PropTypes.func.isRequired, disabled: PropTypes.bool };

const SqlAuthorModal = ({ open, onClose, connections, defaultConnectionId, getEditorSql, aiEnabled, aiProvider, onOpenBuilder, onOpenInEditor, onRunInResult, restore, onView, focus }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const conns = useMemo(() => (connections || []).filter(c => SUPPORTED.includes(c.dbType || 'oracle')), [connections]);
  // 연결은 SQL 편집 탭의 것을 따른다(2026-10-28 사용자 결정 — 데이터 분석·SQL 빌더와 같이). 작업에는 그 연결이 적히고,
  // 편집기 탭의 연결이 바뀌면 연 작업도 그 연결로 옮긴다. 이 화면에서 연결을 따로 고르는 길은 두지 않는다
  const editorConnId = defaultConnectionId || null;
  const editorConnOk = !!(editorConnId && conns.some(c => c.id === editorConnId));
  const editorConnUnsupported = !!(editorConnId && !editorConnOk && (connections || []).some(c => c.id === editorConnId));
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [job, setJob] = useState(null);
  const [d, setDraft] = useState(null);
  const [step, setStep] = useState('request');
  const [creating, setCreating] = useState(null);       // { title, connectionId, request }
  const [busy, setBusy] = useState(null);
  const [simQ, setSimQ] = useState('');
  const [instruction, setInstruction] = useState('');
  const [maxRows, setMaxRows] = useState(50);
  const [result, setResult] = useState(null);           // ④ 결과 — 화면에만
  const stopRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    try { setJobs((await api.listJobs(q)).jobs || []); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [q]);
  useEffect(() => { if (open) reload(); }, [open, reload]);

  const adopt = useCallback((j) => {
    setJob(j); setDraft(j ? JSON.parse(JSON.stringify(j.doc)) : null);
    if (j) setSimQ(j.doc.similar.q || '');
  }, []);

  // 새로고침 뒤 — 보던 작업·단계로 한 번만 되돌린다(App 이 workspaceLayout 에서 준 restore)
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    if (!(restore && restore.id)) { setViewReady(true); return; }
    let alive = true;
    api.getJob(restore.id)
      .then((r) => { if (!alive) return; adopt(r.job); if (restore.step && TABS.includes(restore.step)) setStep(restore.step); })
      .catch(() => { /* 지워진 작업 — 목록만 */ })
      .finally(() => { if (alive) setViewReady(true); });
    return () => { alive = false; };   // eslint-disable-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewJobId = job ? job.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewJobId, step }); }, [open, viewReady, viewJobId, step, onView]);

  const run = async (label, fn) => {
    setBusy(label);
    try { return await fn(); } catch (err) {
      if (!(stopRef.current && stopRef.current.signal.aborted)) toast.error(formatApiError(err, t));
      else toast.info(t('common:sau.stopped'));
      return null;
    } finally { setBusy(null); stopRef.current = null; }
  };
  const running = !!busy;
  const dirty = !!(job && d && JSON.stringify(d) !== JSON.stringify(job.doc));

  const startNew = () => {
    setCreating({ title: '', connectionId: editorConnOk ? editorConnId : '', request: emptyRequest() });
    adopt(null); setResult(null);
  };
  const openJob = (id, wantStep) => run('open', async () => {
    const r = await api.getJob(id);
    adopt(r.job); setCreating(null); setResult(null); setInstruction('');
    setStep(wantStep && TABS.includes(wantStep) ? wantStep : (r.job.status === 'request' ? 'request' : r.job.status));
    reload();
  });
  // 채팅 에이전트가 가리킨 작업으로(sql_author_open) — 화면이 열린 뒤 한 번
  const focusNonce = focus && focus.nonce;
  useEffect(() => {
    if (!open || !focus || !focus.id) return;
    openJob(focus.id, focus.step);
  }, [open, focusNonce]);   // eslint-disable-line react-hooks/exhaustive-deps -- nonce 가 바뀔 때만
  const create = () => run('create', async () => {
    const r = await api.createJob({ connectionId: creating.connectionId, title: creating.title, request: creating.request });
    adopt(r.job); setCreating(null); setStep('similar'); reload();
    // 만들자마자 유사 쿼리를 찾아 둔다 — ② 에 들어가면 바로 고를 수 있게(기계 검색, AI 아님)
    try { const s = await api.searchSimilar(r.job.id, ''); adopt(s.job); } catch (err) { toast.warning(formatApiError(err, t)); }
  });
  const save = (patch, stepName, quiet) => run('save', async () => {
    const r = await api.patchJob(job.id, patch, { step: stepName });
    adopt(r.job); reload();
    if (!quiet) toast.success(t('common:sau.saved'), { autoClose: 1200 });
    return r;
  });
  // 머리줄 「저장」 이 지금 단계에서 저장하는 칸 — 단계마다 맨 아래 있던 저장 단추를 대신한다(2026-10-26, common/WorkflowHead)
  const STEP_SAVE = {
    request: { fields: ['request'], patch: () => ({ request: d.request }) },
    similar: { fields: ['similar'], patch: () => ({ similar: { items: (d.similar.items || []).map(x => ({ id: x.id, verdict: x.verdict, note: x.note })) } }) },
    write: { fields: ['draft'], patch: () => ({ draft: { sql: d.draft.sql, explanation: d.draft.explanation, assumptions: d.draft.assumptions } }) },
  };
  const stepSave = job && d && !job.readOnly ? STEP_SAVE[step] : null;
  const stepIsDirty = !!stepSave && stepDirty(d, job.doc, stepSave.fields);
  const saveStep = () => { if (stepSave) save(stepSave.patch(), step); };
  const removeJob = async () => {
    const ok = await confirm({ title: t('common:sau.deleteTitle'), message: t('common:sau.deleteBody', { title: job.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    await run('delete', async () => { await api.deleteJob(job.id); adopt(null); reload(); });
  };

  // ── ② ──
  const searchSimilar = () => run('similar', async () => {
    if (dirty) await api.patchJob(job.id, { request: d.request }, { step: 'request' });
    adopt((await api.searchSimilar(job.id, simQ)).job);
  });
  const setVerdict = (it, verdict) => {
    const items = d.similar.items.map(x => (x.id === it.id ? { ...x, verdict } : x));
    setDraft({ ...d, similar: { ...d.similar, items } });
    save({ similar: { items: items.map(x => ({ id: x.id, verdict: x.verdict, note: x.note })) } }, 'similar', true);
  };
  const setNote = (it, note) => setDraft({ ...d, similar: { ...d.similar, items: d.similar.items.map(x => (x.id === it.id ? { ...x, note } : x)) } });
  const pickAsRef = (it) => {
    setDraft({ ...d, request: { ...d.request, refSql: it.sql } });
    setStep('request');
    toast.info(t('common:sau.refSet'));
  };

  // ── ③ ──
  const aiWrite = () => run('ai', async () => {
    if (dirty) await api.patchJob(job.id, { request: d.request, draft: { sql: d.draft.sql, explanation: d.draft.explanation, assumptions: d.draft.assumptions } }, { step: 'write' });
    const ctl = new AbortController();
    stopRef.current = ctl;
    const ins = d.draft.sql.trim() ? instruction.trim() : '';
    const r = await api.aiWrite(job.id, { provider: aiProvider, instruction: ins }, ctl.signal);
    adopt(r.job); reload(); setInstruction('');
    if (r.job.doc.draft.status === 'ok') toast.success(t('common:sau.aiOk', { calls: r.calls }));
    else toast.warning(t('common:sau.aiFailed', { error: r.lastError || '' }));
  });
  const checkPlan = () => run('check', async () => {
    adopt((await api.checkPlan(job.id, d.draft.sql)).job);
  });

  // ── ④ ──
  const runPreview = () => run('run', async () => {
    if (dirty) await api.patchJob(job.id, { draft: { sql: d.draft.sql, explanation: d.draft.explanation, assumptions: d.draft.assumptions } }, { step: 'write' });
    setResult(null);
    const r = await api.runPreview(job.id, { sql: d.draft.sql, maxRows });
    adopt(r.job); setResult(r.result);
  });
  const stopRun = async () => { try { await api.cancelRun(job.id); } catch (_e) { /* 이미 끝났다 */ } };
  const copySql = async () => { try { await navigator.clipboard.writeText(d.draft.sql); toast.success(t('common:sau.copied'), { autoClose: 1000 }); } catch (_e) { toast.error(t('common:sau.copyFailed')); } };

  // 작업의 연결을 SQL 편집 탭의 연결로 옮긴다 — 요구의 스키마·테이블은 앞 연결의 것이라 함께 비운다(저장은 사람이)
  const followEditorConnection = (connectionId) => run('conn', async () => {
    if (!connectionId || !job || connectionId === job.connectionId) return;
    const r = await api.setJobConnection(job.id, connectionId);
    const keep = d && dirty ? d.request : null;
    adopt(r.job); reload();
    const base = keep || r.job.doc.request;
    const cleared = !!(base.schema || (base.tables || []).length);
    if (cleared) setDraft(prev => ({ ...prev, request: { ...base, schema: '', tables: [] } }));
    toast.info(t(cleared ? 'common:sau.connFollowedTables' : 'common:sau.connFollowed', { name: r.job.connName || connectionId }));
  });
  const jobConnId = job ? job.connectionId : null;
  useEffect(() => {
    if (!open || !job || job.readOnly || running || !editorConnOk || jobConnId === editorConnId) return;
    followEditorConnection(editorConnId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 작업·작업의 연결·편집기 연결이 바뀔 때만
  }, [open, job && job.id, jobConnId, editorConnId, editorConnOk]);
  // 새 작업을 쓰는 동안 편집기 연결이 바뀌면 — 그 연결로, 고른 스키마·테이블은 비운다
  useEffect(() => {
    setCreating(c => (c && c.connectionId !== (editorConnOk ? editorConnId : '')
      ? { ...c, connectionId: editorConnOk ? editorConnId : '', request: { ...c.request, schema: '', tables: [] } } : c));
  }, [editorConnId, editorConnOk]);
  // 편집기 연결이 없거나 SQL 작성이 받지 않는 DBMS 면 — 새 작업은 만들 수 없고, 연 작업은 마지막으로 적힌 연결로 돈다(그렇다고 말한다)
  const editorConnNote = (forJob) => (editorConnOk ? null : (
    <div className="stn-warn sau-conn-note" role="status">
      {t(editorConnUnsupported ? 'common:sau.editorConnUnsupported' : 'common:sau.editorConnNone')}
      {forJob && forJob.connectionId ? ` ${t('common:sau.jobConnStays', { name: forJob.connName || forJob.connectionId })}` : ''}
    </div>
  ));

  // ── 단계 그리기 ──
  const renderRequest = () => (
    <div className="stn-step">
      <RequestFields value={d.request} onChange={request => setDraft({ ...d, request })} connectionId={job.connectionId} getEditorSql={getEditorSql} disabled={running} />
    </div>
  );

  const renderSimilar = () => {
    const items = d.similar.items || [];
    const fits = items.filter(x => x.verdict === 'fit').length;
    return (
      <div className="stn-step">
        <div className="stn-row">
          <input className="stn-grow" value={simQ} onChange={e => setSimQ(e.target.value)} placeholder={t('common:sau.simQPh')} disabled={running}
            onKeyDown={e => { if (e.key === 'Enter') searchSimilar(); }} />
          <button type="button" className="app-btn app-btn-primary" onClick={searchSimilar} disabled={running}><FiSearch size={12} /> {t('common:sau.searchSimilar')}</button>
        </div>
        <div className="stn-note">{t('common:sau.simHint')}{(d.similar.terms || []).length ? ` · ${t('common:sau.terms', { terms: d.similar.terms.join(', ') })}` : ''}</div>
        {(d.similar.notes || []).map(n => <div key={n} className="sau-notice"><FiAlertTriangle size={11} /> {t(`common:sau.simNote.${n.split(':')[0]}`, { defaultValue: n, detail: n.split(':').slice(1).join(':') })}</div>)}
        {busy === 'similar' && <div className="stn-progress">{t('common:sau.searching')}</div>}
        {d.similar.searchedAt && !items.length && <div className="stn-note">{t('common:sau.noSimilar')}</div>}
        {!d.similar.searchedAt && <div className="stn-note">{t('common:sau.notSearched')}</div>}
        {items.map(it => (
          <SimilarCard key={it.id} it={it} disabled={running} onVerdict={v => setVerdict(it, v)} onNote={v => setNote(it, v)} onUse={() => pickAsRef(it)} />
        ))}
        <div className="stn-actions">
          <span className="stn-note">{t('common:sau.fitCount', { n: fits })}</span>
          <button type="button" className="app-btn app-btn-primary" onClick={() => setStep('write')}>{t('common:sau.next')}</button>
        </div>
      </div>
    );
  };

  const renderWrite = () => {
    const dr = d.draft;
    const plan = job.doc.draft.plan && job.doc.draft.sql === dr.sql ? job.doc.draft.plan : null;
    const has = !!dr.sql.trim();
    return (
      <div className="stn-step">
        {aiEnabled && (
          <div className="stn-row srv-aibar">
            {has && <input className="stn-grow" value={instruction} onChange={e => setInstruction(e.target.value)} placeholder={t('common:sau.instructionPh')} disabled={running} />}
            {busy === 'ai'
              ? <button type="button" className="app-btn app-btn-danger" onClick={() => stopRef.current && stopRef.current.abort()}><FiSquare size={12} /> {t('common:sau.stop')}</button>
              : <button type="button" className="app-btn stn-ai" onClick={aiWrite} disabled={running || !requestOk(d.request)}><FiCpu size={12} /> {has ? t('common:sau.aiRewrite') : t('common:sau.aiWrite')}</button>}
            <span className="stn-note">{t('common:sau.aiUse', { n: (d.similar.items || []).filter(x => x.verdict === 'fit').length })}</span>
          </div>
        )}
        {busy === 'ai' && <div className="stn-progress">{t('common:sau.aiRunning')}</div>}
        {/* 이 창이 AI 패널을 가린다 — 같은 대화로 지금 요구·SQL 을 두고 묻는다(값은 가려서, 그 턴에만). 패널이 없으면 그리지 않는다 */}
        {aiEnabled && <AgentStrip placeholder={t('common:sau.askAgentPh')} getScreen={() => ({ kind: 'sqlAuthor', text: JSON.stringify({
          mode: d.request.mode, requirement: d.request.requirement, schema: d.request.schema, tables: d.request.tables, columns: d.request.columns,
          keywords: d.request.keywords, logic: d.request.logic, sql: maskForAgent(dr.sql), explanation: dr.explanation, assumptions: dr.assumptions,
          planCheck: plan ? (plan.ok ? 'ok' : `failed: ${plan.error}`) : 'not checked', connection: job.connName || null, dbType: job.dbType || null,
        }, null, 1) })} />}
        <label className="stn-field"><span className="stn-row stn-between">
          <span>{t('common:sau.sql')} {dr.origin === 'ai' && job.doc.draft.sql === dr.sql && <span className="stn-badge">AI</span>}</span>
          <span className="stn-row">
            <button type="button" className="app-btn" onClick={copySql} disabled={!has}><FiCopy size={12} /> {t('common:sau.copy')}</button>
            {onOpenBuilder && <button type="button" className="app-btn" onClick={() => onOpenBuilder(dr.sql)} disabled={!has} title={t('common:sau.openBuilderHint')}><FiLayers size={12} /> {t('common:sau.openBuilder')}</button>}
          </span>
        </span>
          <textarea className="srv-mono" rows={14} value={dr.sql} onChange={e => setDraft({ ...d, draft: { ...dr, sql: e.target.value } })} placeholder={t('common:sau.sqlPh')} disabled={running} />
        </label>
        <div className="stn-grid2">
          <label className="stn-field"><span>{t('common:sau.explanation')}</span><textarea rows={5} value={dr.explanation} onChange={e => setDraft({ ...d, draft: { ...dr, explanation: e.target.value } })} disabled={running} /></label>
          <label className="stn-field"><span>{t('common:sau.assumptions')}</span><textarea rows={5} value={dr.assumptions} onChange={e => setDraft({ ...d, draft: { ...dr, assumptions: e.target.value } })} disabled={running} /></label>
        </div>
        <div className="stn-box">
          <div className="stn-row stn-between">
            <b>{t('common:sau.planCheck')}</b>
            <button type="button" className="app-btn" onClick={checkPlan} disabled={running || !has}><FiCheckCircle size={12} /> {t('common:sau.checkPlan')}</button>
          </div>
          {!plan && <span className="stn-note">{has ? t('common:sau.planNotChecked') : t('common:sau.planHint')}</span>}
          {plan && plan.ok && <div className="sau-ok sau-fade"><FiCheckCircle size={12} /> {t('common:sau.planOk')}</div>}
          {plan && !plan.ok && <div className="sau-bad sau-fade"><FiAlertTriangle size={12} /> {job.doc.draft.origin === 'ai' ? t('common:sau.planUnresolved') : t('common:sau.planFail')} <span className="mono">{plan.error}</span></div>}
          {plan && plan.ok && plan.planText && <pre className="sau-plan">{monoWideText(plan.planText)}</pre>}
          {(job.doc.draft.attempts || []).length > 0 && job.doc.draft.sql === dr.sql && (
            <details className="sau-attempts"><summary>{t('common:sau.attempts', { n: job.doc.draft.attempts.length })}</summary>
              <ol>{job.doc.draft.attempts.map((a, i) => <li key={`${i}-${a.error}`} className="mono">{a.error}</li>)}</ol>
            </details>
          )}
        </div>
        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={() => setStep('run')} disabled={!has}>{t('common:sau.next')}</button>
        </div>
      </div>
    );
  };

  const renderRun = () => {
    const has = !!d.draft.sql.trim();
    const last = job.doc.run;
    return (
      <div className="stn-step">
        <div className="stn-row">
          <label className="stn-row">{t('common:sau.maxRows')}
            <select value={maxRows} onChange={e => setMaxRows(Number(e.target.value))} disabled={running}>
              {ROW_CHOICES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          {busy === 'run'
            ? <button type="button" className="app-btn app-btn-danger" onClick={stopRun}><FiSquare size={12} /> {t('common:sau.stop')}</button>
            : <button type="button" className="app-btn app-btn-primary" onClick={runPreview} disabled={running || !has}><FiPlay size={12} /> {t('common:sau.runPartial')}</button>}
          {/* 전체 실행 — 이 탭 아래의 결과 영역(SQL 편집과 같은 결과 탭)으로(2026-10-19). 연결이 편집기 탭과 다르면 App 이 새 편집기 탭으로 돌린다 */}
          {onRunInResult && <button type="button" className="app-btn" onClick={() => onRunInResult(d.draft.sql, job.connectionId)} disabled={!has} title={t('common:sau.runInResultHint')}><FiPlay size={12} /> {t('common:sau.runInResult')}</button>}
          {onOpenInEditor && <button type="button" className="app-btn" onClick={() => onOpenInEditor(d.draft.sql)} disabled={!has} title={t('common:sau.runFullHint')}><FiExternalLink size={12} /> {t('common:sau.runFull')}</button>}
        </div>
        <div className="stn-note">{t('common:sau.runHint')}</div>
        {busy === 'run' && <div className="stn-progress">{t('common:sau.runRunning')}</div>}
        {!result && last && (
          <div className="stn-note">{last.ok
            ? t('common:sau.lastRun', { at: storedTime(last.at, 'min'), rows: fmt(last.rowCount), more: last.truncated ? '+' : '', ms: fmt(last.elapsedMs) })
            : t('common:sau.lastRunFail', { at: storedTime(last.at, 'min'), error: last.error })}</div>
        )}
        {result && (
          <div className="sau-fade">
            <div className="stn-note">{t('common:sau.result', { rows: fmt(result.rows.length), more: result.truncated ? '+' : '', ms: fmt(result.elapsedMs) })}{result.truncated ? ` · ${t('common:sau.truncated', { n: result.maxRows })}` : ''}</div>
            <div className="stn-table-wrap sau-grid">
              <table className="stn-table">
                <thead><tr><th className="sau-rn">#</th>{result.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- 결과 행은 순서가 곧 키다
                    <tr key={i}><td className="sau-rn">{i + 1}</td>{r.map((v, j) => <td key={result.columns[j] || j} className={v == null ? 'sau-null' : ''}>{v == null ? '(null)' : String(v)}</td>)}</tr>
                  ))}
                  {!result.rows.length && <tr><td colSpan={result.columns.length + 1} className="stn-note">{t('common:sau.noRows')}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHistory = () => {
    const hist = [...(job.doc.history || [])].reverse();
    return (
      <div className="stn-step">
        <div className="stn-table-wrap">
          <table className="stn-table">
            <thead><tr><th>{t('common:sau.histAt')}</th><th>{t('common:sau.histWho')}</th><th>{t('common:sau.histStep')}</th><th>{t('common:sau.histWhat')}</th><th>{t('common:sau.histNote')}</th></tr></thead>
            <tbody>
              {hist.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
                <tr key={i}>
                  <td className="mono">{storedTime(h.at, 'min')}</td>
                  <td>{t(`common:sau.actor.${h.actor}`, { defaultValue: h.actor })}</td>
                  <td>{h.step ? t(`common:sau.steps.${h.step}`, { defaultValue: h.step }) : '-'}</td>
                  <td>{t(`common:sau.action.${h.action}`, { defaultValue: h.action })}</td>
                  <td className="stn-small">{h.note}</td>
                </tr>
              ))}
              {!hist.length && <tr><td colSpan={5} className="stn-note">{t('common:sau.noHistory')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const body = !job || !d ? null : { request: renderRequest, similar: renderSimilar, write: renderWrite, run: renderRun, history: renderHistory }[step]();

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('common:sau.windowTitle')} icon={<FiEdit3 size={14} />} className="app-modal-default stn-modal"
      titleExtra={<EditorConnectionChip connections={connections} value={editorConnId} />}>
      <div className="stn-body">
        <aside className="stn-side">
          <button type="button" className="app-btn app-btn-primary stn-full" onClick={startNew}><FiPlus size={12} /> {t('common:sau.newJob')}</button>
          <input className="stn-full" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:sau.jobSearch')} onKeyDown={e => { if (e.key === 'Enter') reload(); }} />
          <ul className="stn-jobs">
            {jobs.map(j => (
              <li key={j.id} className={job && job.id === j.id ? 'on' : ''} onClick={() => openJob(j.id)}>
                <div className="stn-job-title">{j.title}</div>
                <div className="stn-job-meta">{t(`common:sau.steps.${j.status}`, { defaultValue: j.status })} · {j.connName || '-'} · {storedTime(j.updatedAt, 'date')}</div>
              </li>
            ))}
            {!jobs.length && <li className="stn-empty">{t('common:sau.noJobs')}</li>}
          </ul>
        </aside>
        <section className="stn-main">
          {creating && (
            <div className="stn-new">
              {editorConnNote(null)}
              <div className="stn-grid2">
                <label className="stn-field"><span>{t('common:sau.title')}</span>
                  <input value={creating.title} onChange={e => setCreating({ ...creating, title: e.target.value })} placeholder={t('common:sau.titlePh')} />
                </label>
              </div>
              <RequestFields value={creating.request} onChange={request => setCreating({ ...creating, request })} connectionId={creating.connectionId} getEditorSql={getEditorSql} disabled={running} />
              <div className="stn-actions">
                <button type="button" className="app-btn" onClick={() => setCreating(null)}>{t('common:ui.cancel')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={create} disabled={running || !creating.connectionId || !requestOk(creating.request)}><FiPlus size={12} /> {t('common:sau.create')}</button>
              </div>
            </div>
          )}
          {!creating && !job && <div className="stn-empty-main">{t('common:sau.intro')}</div>}
          {!creating && job && d && (
            <>
              <WorkflowHead title={job.title}
                meta={`${job.connName || job.connectionId} · ${t(`common:sau.modes.${d.request.mode}`)}`}
                badges={job.readOnly ? <span className="stn-warn">{t('common:sau.readOnly')}</span> : null}
                dirty={stepIsDirty}
                actions={job.doc.ai && job.doc.ai.calls > 0 ? <span className="stn-note">{t('common:sau.aiUsage', { calls: job.doc.ai.calls, tokens: fmt(job.doc.ai.tokens), cost: job.doc.ai.costUsd })}</span> : null}
                onSave={stepSave ? saveStep : null} saveDisabled={running || !stepIsDirty || (step === 'request' && !requestOk(d.request))}
                onDelete={removeJob} deleteDisabled={running} />
              <WorkflowRibbon tabs={TABS} step={step} onStep={setStep} label={s => t(`common:sau.steps.${s}`)}
                suffix={(s) => {
                  if (s === 'similar' && (d.similar.items || []).some(x => x.verdict === 'fit')) return ` (${d.similar.items.filter(x => x.verdict === 'fit').length})`;
                  if (s === 'write' && job.doc.draft.status === 'ok') return ' ✓';
                  if (s === 'history' && (job.doc.history || []).length > 0) return ` (${job.doc.history.length})`;
                  return '';
                }} />
              {step !== 'history' && <div className="srv-stepHelp" key={`help-${step}`}>{t(`common:sau.stepHelp.${step}`)}</div>}
              {editorConnNote(job)}
              <div className="stn-step-wrap" key={`${job.id}-${step}`}>{body}</div>
            </>
          )}
        </section>
      </div>
    </AppModal>
  );
};

SqlAuthorModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** 고를 수 있는 연결 — Oracle·Tibero·PostgreSQL 만 */
  connections: PropTypes.arrayOf(PropTypes.object),
  defaultConnectionId: PropTypes.string,
  /** 편집기의 선택 영역·커서 문장 { sql } — 「편집기에서 가져오기」 */
  getEditorSql: PropTypes.func,
  /** AI 빌드인가 — 아니면 ③ 의 AI 단추가 없다(손으로 쓰고 기계 확인은 된다) */
  aiEnabled: PropTypes.bool,
  aiProvider: PropTypes.string,
  /** SQL 빌더로 열기 (sql) */
  onOpenBuilder: PropTypes.func,
  /** 편집기 새 탭으로 (sql) — 전체 실행은 거기서 */
  onOpenInEditor: PropTypes.func,
  onRunInResult: PropTypes.func,
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  /** 채팅 에이전트가 가리킨 작업(SSE open_screen, 2026-10-20) — nonce 가 바뀔 때마다 그 작업·단계를 연다 */
  focus: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string, nonce: PropTypes.number }),
  onView: PropTypes.func,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SqlAuthorModal);
